import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useResilientMutation } from '@/lib/use-resilient-mutation';
import { normalizeQueryFilter } from '@/lib/normalize-query-filter';
import { listCacheFallback } from '@/lib/list-cache-fallback';
import type {
  Representative,
  RepresentativeFilters,
  CreateRepresentativeInput,
  UpdateRepresentativeInput,
} from '@erp/data-access/interfaces';

const REPS_KEY = ['representatives'] as const;
const OFFLINE_PREFIX = 'OFFLINE-';

export function useRepresentatives(filters?: RepresentativeFilters) {
  const ds = useDataService();
  const normalized = normalizeQueryFilter(filters);
  return useQuery({
    queryKey: normalized === undefined ? REPS_KEY : [...REPS_KEY, normalized],
    queryFn: () => ds.representatives.list(normalized),
  });
}

export function useRepresentative(id: string) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...REPS_KEY, id],
    queryFn: () => ds.representatives.getById(id),
    enabled: !!id,
    placeholderData: () => listCacheFallback<Representative>(qc, REPS_KEY, id),
  });
}

export function useRepresentativeClients(representativeId: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...REPS_KEY, representativeId, 'clients'],
    queryFn: () => ds.representatives.listClientsOf(representativeId),
    enabled: !!representativeId,
  });
}

export function useClientRepresentatives(clientId: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: ['clients', clientId, 'representatives'],
    queryFn: () => ds.representatives.listByClient(clientId),
    enabled: !!clientId,
  });
}

export function useCreateRepresentative() {
  const ds = useDataService();
  return useResilientMutation<CreateRepresentativeInput, Representative>({
    entity: 'representatives',
    op: 'create',
    invalidate: [REPS_KEY],
    online: (input) => ds.representatives.create(input),
    optimistic: (input, clientId) =>
      ({
        id: clientId,
        name: input.name ?? OFFLINE_PREFIX + clientId.slice(0, 6).toUpperCase(),
        phone: input.phone ?? null,
        email: input.email ?? null,
        notes: input.notes ?? null,
        commissionRate: input.commissionRate ?? 0,
        createdAt: new Date().toISOString(),
      }) as Representative,
    applyOptimistic: (qc, rep) => {
      qc.setQueriesData<Representative[]>({ queryKey: REPS_KEY }, (old) => {
        if (!Array.isArray(old)) return old;
        if (old.some((r) => r.id === rep.id)) return old;
        return [...old, rep].sort((a, b) => a.name.localeCompare(b.name));
      });
    },
  });
}

export function useUpdateRepresentative() {
  const ds = useDataService();
  return useResilientMutation<{ id: string; input: UpdateRepresentativeInput }, Representative>({
    entity: 'representatives',
    op: 'update',
    invalidate: [REPS_KEY],
    online: ({ id, input }) => ds.representatives.update(id, input),
    optimistic: ({ id, input }) =>
      ({
        id,
        name: input.name ?? '(édité hors-ligne)',
        phone: input.phone ?? null,
        email: input.email ?? null,
        notes: input.notes ?? null,
        commissionRate: input.commissionRate ?? 0,
      }) as Representative,
    applyOptimistic: (qc, updated, { id, input }) => {
      qc.setQueriesData<Representative[]>({ queryKey: REPS_KEY }, (old) =>
        Array.isArray(old) ? old.map((r) => (r.id === id ? { ...r, ...input } : r)) : old,
      );
      qc.setQueryData<Representative>([...REPS_KEY, id], updated);
    },
  });
}

export function useDeleteRepresentative() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'representatives',
    op: 'delete',
    invalidate: [REPS_KEY],
    online: (id) => ds.representatives.delete(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _result, id) => {
      qc.setQueriesData<Representative[]>({ queryKey: REPS_KEY }, (old) =>
        Array.isArray(old) ? old.filter((r) => r.id !== id) : old,
      );
      qc.removeQueries({ queryKey: [...REPS_KEY, id] });
    },
  });
}

// Linking is a "custom" operation — joins a client to a rep via the
// pivot table. Stays online-only for now: linking offline would require
// an optimistic patch on the dependent query caches and the order of
// operations matters (the client + rep must both already exist on the
// server). We can add it once both ends are consistently offline-aware.
export function useLinkRepresentativeToClient() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useResilientMutation<{ clientId: string; representativeId: string }, void>({
    entity: 'representatives',
    op: 'custom',
    method: 'linkClient',
    online: ({ clientId, representativeId }) =>
      ds.representatives.linkClient(clientId, representativeId),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _r, vars) => {
      qc.invalidateQueries({ queryKey: ['clients', vars.clientId, 'representatives'] });
      qc.invalidateQueries({ queryKey: [...REPS_KEY, vars.representativeId, 'clients'] });
    },
    onSuccess: (_r, vars) => {
      qc.invalidateQueries({ queryKey: ['clients', vars.clientId, 'representatives'] });
      qc.invalidateQueries({ queryKey: [...REPS_KEY, vars.representativeId, 'clients'] });
    },
  });
}

export function useUnlinkRepresentativeFromClient() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useResilientMutation<{ clientId: string; representativeId: string }, void>({
    entity: 'representatives',
    op: 'custom',
    method: 'unlinkClient',
    online: ({ clientId, representativeId }) =>
      ds.representatives.unlinkClient(clientId, representativeId),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _r, vars) => {
      qc.invalidateQueries({ queryKey: ['clients', vars.clientId, 'representatives'] });
      qc.invalidateQueries({ queryKey: [...REPS_KEY, vars.representativeId, 'clients'] });
    },
    onSuccess: (_r, vars) => {
      qc.invalidateQueries({ queryKey: ['clients', vars.clientId, 'representatives'] });
      qc.invalidateQueries({ queryKey: [...REPS_KEY, vars.representativeId, 'clients'] });
    },
  });
}
